// Browser-side simulator for the foyrmind prompt optimizer. Same
// contract as the Python ensemble: takes an OptimizerSeed, returns
// an OptimizerResult with one DSPy and one AdalFlow variant scored
// against the shared rubric. The actual prompt rewriting is mocked
// (string transforms over the YAML body) because the real backend
// requires a live LM round-trip via APISIX. The scoring path itself
// is REAL — same rubric the Python module evaluates against.

import { scoreAnswer } from "./rubric";
import type { OptimizerSeed, OptimizerResult, PromptVariant } from "./types";

function uuid(): string {
  // RFC4122 v4 — fine for client-side variant IDs (not crypto).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function rubricAvg(yaml: string, seed: OptimizerSeed): number {
  const samples = [...seed.failing, ...seed.golden];
  if (samples.length === 0) return 0;
  const totals = samples.map((s) => scoreAnswer({ answer: yaml, sample: s, voice: seed.designer_voice }).total);
  return Math.round((totals.reduce((a, b) => a + b, 0) / samples.length) * 1000) / 1000;
}

// ── per-backend deterministic rewriters ──────────────────────────────
// These are deliberately simple. They demonstrate WHAT the two
// backends are trying to maximise — the real LM-driven backends will
// outperform these by a wide margin, but the rubric output here is
// directionally correct so the side-by-side UI makes sense.

function dspyRewrite(yaml: string, seed: OptimizerSeed): string {
  const newRules: string[] = [];
  for (const s of seed.failing) {
    for (const m of s.must_contain ?? []) {
      newRules.push(`  - Always mention "${m}" when answering this scenario.`);
    }
    for (const f of s.must_not_contain ?? []) {
      newRules.push(`  - Never reply with "${f}" — propose alternatives instead.`);
    }
    for (const o of s.ontology_terms ?? []) {
      newRules.push(`  - Cite \`${o}\` when the question touches ${s.room_kind ?? "this scope"}.`);
    }
  }
  const tail =
    "\n## DSPy MIPROv2 — compiled rules from failing examples\n" +
    Array.from(new Set(newRules)).slice(0, 8).join("\n");
  return yaml.trim() + tail;
}

function adalflowRewrite(yaml: string, seed: OptimizerSeed): string {
  // Text-Grad style: prepend an "after reviewing failures" guidance block.
  const head =
    "## AdalFlow Text-Grad — voice-tuned for " + seed.designer_voice + "\n" +
    seed.failing
      .map((s, i) => `# fail-${i + 1}: ${s.query} → expected: ${s.expected}`)
      .join("\n") +
    "\n";
  return head + yaml.trim();
}

function fakeDuration(seedSize: number, backend: "dspy" | "adalflow"): number {
  // Realistic-ish numbers so the UI badges look right.
  const base = backend === "dspy" ? 4.2 : 2.9;
  return Math.round((base + seedSize * 0.18) * 100) / 100;
}

export async function runMockOptimizer(seed: OptimizerSeed, opts: { minLift?: number } = {}): Promise<OptimizerResult> {
  const minLift = opts.minLift ?? 0.25;
  const baseline = rubricAvg(seed.current_yaml, seed);

  const dspyYaml = dspyRewrite(seed.current_yaml, seed);
  const adalYaml = adalflowRewrite(seed.current_yaml, seed);

  const dspyScore = rubricAvg(dspyYaml, seed);
  const adalScore = rubricAvg(adalYaml, seed);

  const now = new Date().toISOString();
  const seedSize = seed.failing.length + seed.golden.length;

  const variants: PromptVariant[] = [
    {
      variant_id: uuid(),
      target_prompt: seed.target_prompt,
      backend: "dspy",
      proposed_yaml: dspyYaml,
      proposer_log: [
        `[dspy] LM=openai/${"foyrmind-chat"} via apisix.hypermind-data`,
        `[dspy] training set size=${seedSize}`,
        `[dspy] compiler=miprov2`,
        `[dspy] proposed body size=${dspyYaml.length} chars`,
      ].join("\n"),
      duration_seconds: fakeDuration(seedSize, "dspy"),
      avg_score: dspyScore,
      lift_vs_current: Math.round((dspyScore - baseline) * 1000) / 1000,
      proposed_at: now,
    },
    {
      variant_id: uuid(),
      target_prompt: seed.target_prompt,
      backend: "adalflow",
      proposed_yaml: adalYaml,
      proposer_log: [
        `[adalflow] proposer=foyrmind-chat via apisix.hypermind-data`,
        `[adalflow] iter=0 baseline_score=${baseline.toFixed(3)}`,
        `[adalflow] iter=1 score=${adalScore.toFixed(3)}`,
        `[adalflow] propose took ${fakeDuration(seedSize, "adalflow")}s`,
      ].join("\n"),
      duration_seconds: fakeDuration(seedSize, "adalflow"),
      avg_score: adalScore,
      lift_vs_current: Math.round((adalScore - baseline) * 1000) / 1000,
      proposed_at: now,
    },
  ];

  const best = variants.reduce<PromptVariant>((acc, v) => (v.lift_vs_current > acc.lift_vs_current ? v : acc), variants[0]!);
  const winner = best.lift_vs_current >= minLift ? best : null;

  // Simulate a small runtime cost so the UI shows the spinner.
  await new Promise<void>((res) => setTimeout(res, 700));

  return {
    seed,
    variants,
    winner,
    staged_path: winner ? `prompts/_proposed/${seed.target_prompt}__${now.slice(0, 10)}__${winner.backend}.yaml` : null,
    baseline_score: baseline,
  };
}
